/**
 * /admin/profile — self-service profile management.
 *
 * Lets the logged-in user:
 *   - See their email, role, and last-login info
 *   - Change their password (requires current password; revokes other sessions)
 *   - See all their active sessions
 *   - Revoke individual sessions or logout-everywhere
 *
 * Available to both admin and user roles.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authApi } from '@/api/client';
import {
  Button,
  Input,
  Card,
  CardContent,
  Spinner,
} from '@/components/common';
import {
  UserCog,
  Lock,
  AlertTriangle,
  CheckCircle2,
  LogOut,
  Monitor,
  Trash2,
} from 'lucide-react';

interface SessionItem {
  id: string;
  created_at: string;
  last_used_at: string;
  ip: string | null;
  user_agent: string | null;
  remember_me: boolean;
  is_current: boolean;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function uaShort(ua: string | null): string {
  if (!ua) return 'unknown';
  if (ua.length <= 80) return ua;
  return ua.slice(0, 77) + '…';
}

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  // Password change form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState<string | null>(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState<SessionItem[] | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const policyChecks = [
    { label: 'at least 12 characters', ok: newPw.length >= 12 },
    { label: 'one uppercase letter', ok: /[A-Z]/.test(newPw) },
    { label: 'one lowercase letter', ok: /[a-z]/.test(newPw) },
    { label: 'one digit', ok: /[0-9]/.test(newPw) },
    { label: 'one symbol', ok: /[^A-Za-z0-9]/.test(newPw) },
  ];
  const policyOk = policyChecks.every((c) => c.ok);
  const passwordsMatch = newPw.length > 0 && newPw === confirmPw;

  const loadSessions = async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const rows = await authApi.listSessions();
      setSessions(rows);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'could not load sessions';
      setSessionsError(typeof detail === 'string' ? detail : 'could not load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(null);

    if (!policyOk) {
      setPwError('new password does not meet the policy below');
      return;
    }
    if (!passwordsMatch) {
      setPwError('new passwords do not match');
      return;
    }

    setPwSubmitting(true);
    try {
      const resp = await authApi.changePassword(currentPw, newPw);
      setPwSuccess(resp.message || 'password updated');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      // Other sessions revoked; reload current sessions list
      loadSessions();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail;
      let msg = 'could not change password';
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        msg = `password rejected: ${detail.errors.join('; ')}`;
      }
      setPwError(msg);
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this session? The user on that device will be signed out immediately.')) {
      return;
    }
    try {
      await authApi.revokeSession(id);
      loadSessions();
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail || 'could not revoke';
      alert(`Revoke failed: ${typeof detail === 'string' ? detail : 'unknown error'}`);
    }
  };

  const handleLogoutEverywhere = async () => {
    if (!window.confirm('Logout everywhere? You will be signed out of all devices including this one.')) {
      return;
    }
    try {
      await authApi.revokeAllSessions();
    } catch {
      // ignore
    }
    await refresh();
    navigate('/login');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Profile card */}
      <Card>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
              <UserCog size={28} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{user?.email}</h2>
              <p className="text-sm text-gray-500 capitalize">{user?.role} · DDARP Manager</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Last sign-in</p>
              <p className="font-medium text-gray-900">{formatDate(user?.lastLoginAt)}</p>
            </div>
            <div>
              <p className="text-gray-500">Last IP</p>
              <p className="font-medium text-gray-900">{user?.lastLoginIp || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card>
        <CardContent>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Change password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {pwError && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600">
                <AlertTriangle size={20} />
                <span className="text-sm">{pwError}</span>
              </div>
            )}
            {pwSuccess && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700">
                <CheckCircle2 size={20} />
                <span className="text-sm">{pwSuccess}</span>
              </div>
            )}

            <Input
              label="Current password"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              leftIcon={<Lock size={18} />}
              fullWidth
              required
              autoComplete="current-password"
              disabled={pwSubmitting}
            />
            <Input
              label="New password"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              leftIcon={<Lock size={18} />}
              fullWidth
              required
              autoComplete="new-password"
              disabled={pwSubmitting}
            />
            <Input
              label="Confirm new password"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              leftIcon={<Lock size={18} />}
              fullWidth
              required
              autoComplete="new-password"
              disabled={pwSubmitting}
            />

            <ul className="space-y-1 text-xs">
              {policyChecks.map((c) => (
                <li key={c.label} className={c.ok ? 'text-emerald-600' : 'text-gray-500'}>
                  {c.ok ? '✓' : '○'} {c.label}
                </li>
              ))}
              <li className={passwordsMatch ? 'text-emerald-600' : 'text-gray-500'}>
                {passwordsMatch ? '✓' : '○'} passwords match
              </li>
            </ul>

            <Button
              type="submit"
              variant="primary"
              isLoading={pwSubmitting}
              disabled={!currentPw || !policyOk || !passwordsMatch || pwSubmitting}
            >
              Update password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Active sessions */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active sessions</h3>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<LogOut size={16} />}
              onClick={handleLogoutEverywhere}
            >
              Logout everywhere
            </Button>
          </div>

          {sessionsLoading && (
            <div className="py-6 text-center">
              <Spinner />
            </div>
          )}

          {sessionsError && (
            <p className="text-red-600 text-sm">{sessionsError}</p>
          )}

          {sessions && sessions.length === 0 && (
            <p className="text-gray-500 text-sm">No active sessions.</p>
          )}

          {sessions && sessions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-3">Device</th>
                    <th className="py-2 pr-3">IP</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">Last used</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-b border-gray-100">
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <Monitor size={16} className="text-gray-400" />
                          <span title={s.user_agent ?? ''}>{uaShort(s.user_agent)}</span>
                          {s.is_current && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">current</span>
                          )}
                          {s.remember_me && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">remembered</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{s.ip || '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(s.created_at)}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(s.last_used_at)}</td>
                      <td className="py-2 text-right">
                        {!s.is_current && (
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Trash2 size={14} />}
                            onClick={() => handleRevoke(s.id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Button variant="ghost" onClick={async () => { await logout(); navigate('/login'); }}>
        Sign out of this device
      </Button>
    </div>
  );
}
